import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-quotes-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ─────────────────────────────────────────────────────────────── */

type CoverageAmt = '100' | '250' | '500' | '750' | '1000';
type TermLen = '10' | '20' | '30' | 'whole';
type HealthClass = 'preferred_plus' | 'preferred' | 'standard_plus' | 'standard';
type AgeKey = '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60';

/* ─── Carrier Data ───────────────────────────────────────────────────────── */

interface CarrierRate {
  carrier: string;
  amBest: string;
  logo: string;
  color: string;
  specialty: string;
  noExam: boolean;
  rates: Record<AgeKey, Record<TermLen, Record<CoverageAmt, number>>>;
}

const CARRIERS: CarrierRate[] = [
  {
    carrier: 'Banner Life / Legal & General',
    amBest: 'A+',
    logo: 'ri-shield-star-line',
    color: '#1A3A52',
    specialty: 'Lowest term rates overall',
    noExam: false,
    rates: {
      '25': { '10': { '100': 9, '250': 13, '500': 20, '750': 28, '1000': 35 }, '20': { '100': 12, '250': 17, '500': 27, '750': 38, '1000': 48 }, '30': { '100': 16, '250': 22, '500': 36, '750': 51, '1000': 65 }, 'whole': { '100': 95, '250': 220, '500': 430, '750': 635, '1000': 840 } },
      '30': { '10': { '100': 10, '250': 14, '500': 22, '750': 31, '1000': 40 }, '20': { '100': 13, '250': 19, '500': 30, '750': 43, '1000': 55 }, '30': { '100': 18, '250': 25, '500': 42, '750': 60, '1000': 80 }, 'whole': { '100': 115, '250': 265, '500': 520, '750': 770, '1000': 1020 } },
      '35': { '10': { '100': 11, '250': 15, '500': 26, '750': 36, '1000': 48 }, '20': { '100': 15, '250': 22, '500': 38, '750': 55, '1000': 72 }, '30': { '100': 21, '250': 30, '500': 54, '750': 78, '1000': 104 }, 'whole': { '100': 145, '250': 335, '500': 660, '750': 980, '1000': 1300 } },
      '40': { '10': { '100': 14, '250': 20, '500': 36, '750': 52, '1000': 68 }, '20': { '100': 20, '250': 32, '500': 58, '750': 85, '1000': 112 }, '30': { '100': 30, '250': 46, '500': 84, '750': 124, '1000': 164 }, 'whole': { '100': 190, '250': 445, '500': 880, '750': 1310, '1000': 1740 } },
      '45': { '10': { '100': 19, '250': 28, '500': 52, '750': 76, '1000': 100 }, '20': { '100': 30, '250': 48, '500': 88, '750': 130, '1000': 172 }, '30': { '100': 48, '250': 72, '500': 136, '750': 200, '1000': 268 }, 'whole': { '100': 255, '250': 595, '500': 1180, '750': 1760, '1000': 2340 } },
      '50': { '10': { '100': 28, '250': 40, '500': 76, '750': 112, '1000': 148 }, '20': { '100': 48, '250': 72, '500': 138, '750': 204, '1000': 272 }, '30': { '100': 80, '250': 115, '500': 218, '750': 322, '1000': 432 }, 'whole': { '100': 345, '250': 810, '500': 1610, '750': 2410, '1000': 3210 } },
      '55': { '10': { '100': 42, '250': 62, '500': 118, '750': 175, '1000': 232 }, '20': { '100': 78, '250': 118, '500': 228, '750': 338, '1000': 448 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 470, '250': 1110, '500': 2210, '750': 3310, '1000': 4410 } },
      '60': { '10': { '100': 68, '250': 102, '500': 196, '750': 292, '1000': 388 }, '20': { '100': 138, '250': 210, '500': 408, '750': 608, '1000': 808 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 650, '250': 1540, '500': 3070, '750': 4600, '1000': 6130 } },
    },
  },
  {
    carrier: 'Protective Life',
    amBest: 'A+',
    logo: 'ri-shield-check-line',
    color: '#D97642',
    specialty: 'Best 30-year term rates',
    noExam: false,
    rates: {
      '25': { '10': { '100': 10, '250': 14, '500': 22, '750': 30, '1000': 38 }, '20': { '100': 13, '250': 18, '500': 29, '750': 41, '1000': 52 }, '30': { '100': 17, '250': 23, '500': 38, '750': 54, '1000': 69 }, 'whole': { '100': 98, '250': 228, '500': 448, '750': 660, '1000': 875 } },
      '30': { '10': { '100': 11, '250': 15, '500': 24, '750': 33, '1000': 43 }, '20': { '100': 14, '250': 20, '500': 32, '750': 46, '1000': 59 }, '30': { '100': 19, '250': 26, '500': 44, '750': 63, '1000': 84 }, 'whole': { '100': 118, '250': 275, '500': 540, '750': 800, '1000': 1060 } },
      '35': { '10': { '100': 12, '250': 16, '500': 28, '750': 38, '1000': 51 }, '20': { '100': 16, '250': 23, '500': 40, '750': 58, '1000': 76 }, '30': { '100': 22, '250': 31, '500': 56, '750': 82, '1000': 108 }, 'whole': { '100': 150, '250': 348, '500': 686, '750': 1018, '1000': 1350 } },
      '40': { '10': { '100': 15, '250': 22, '500': 38, '750': 55, '1000': 72 }, '20': { '100': 22, '250': 34, '500': 62, '750': 90, '1000': 118 }, '30': { '100': 32, '250': 48, '500': 88, '750': 130, '1000': 172 }, 'whole': { '100': 198, '250': 462, '500': 914, '750': 1362, '1000': 1810 } },
      '45': { '10': { '100': 20, '250': 30, '500': 55, '750': 80, '1000': 106 }, '20': { '100': 32, '250': 51, '500': 93, '750': 137, '1000': 181 }, '30': { '100': 51, '250': 76, '500': 143, '750': 210, '1000': 281 }, 'whole': { '100': 265, '250': 618, '500': 1226, '750': 1830, '1000': 2434 } },
      '50': { '10': { '100': 30, '250': 43, '500': 80, '750': 118, '1000': 156 }, '20': { '100': 51, '250': 76, '500': 145, '750': 214, '1000': 285 }, '30': { '100': 84, '250': 121, '500': 229, '750': 338, '1000': 449 }, 'whole': { '100': 358, '250': 840, '500': 1672, '750': 2502, '1000': 3334 } },
      '55': { '10': { '100': 45, '250': 66, '500': 124, '750': 184, '1000': 244 }, '20': { '100': 82, '250': 124, '500': 239, '750': 355, '1000': 470 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 488, '250': 1152, '500': 2296, '750': 3438, '1000': 4582 } },
      '60': { '10': { '100': 72, '250': 108, '500': 206, '750': 307, '1000': 408 }, '20': { '100': 145, '250': 220, '500': 428, '750': 638, '1000': 848 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 675, '250': 1598, '500': 3188, '750': 4778, '1000': 6368 } },
    },
  },
  {
    carrier: 'Pacific Life',
    amBest: 'A+',
    logo: 'ri-anchor-line',
    color: '#2E6B8A',
    specialty: 'Best for high-income earners',
    noExam: false,
    rates: {
      '25': { '10': { '100': 10, '250': 14, '500': 21, '750': 29, '1000': 37 }, '20': { '100': 13, '250': 18, '500': 28, '750': 40, '1000': 51 }, '30': { '100': 17, '250': 23, '500': 37, '750': 53, '1000': 67 }, 'whole': { '100': 100, '250': 232, '500': 456, '750': 672, '1000': 890 } },
      '30': { '10': { '100': 11, '250': 15, '500': 23, '750': 32, '1000': 42 }, '20': { '100': 14, '250': 20, '500': 31, '750': 45, '1000': 58 }, '30': { '100': 19, '250': 26, '500': 43, '750': 62, '1000': 82 }, 'whole': { '100': 120, '250': 280, '500': 550, '750': 815, '1000': 1080 } },
      '35': { '10': { '100': 12, '250': 16, '500': 27, '750': 37, '1000': 50 }, '20': { '100': 16, '250': 23, '500': 39, '750': 57, '1000': 74 }, '30': { '100': 22, '250': 31, '500': 55, '750': 80, '1000': 106 }, 'whole': { '100': 152, '250': 354, '500': 698, '750': 1036, '1000': 1374 } },
      '40': { '10': { '100': 15, '250': 21, '500': 37, '750': 54, '1000': 70 }, '20': { '100': 21, '250': 33, '500': 60, '750': 88, '1000': 116 }, '30': { '100': 31, '250': 47, '500': 86, '750': 128, '1000': 168 }, 'whole': { '100': 200, '250': 468, '500': 928, '750': 1384, '1000': 1840 } },
      '45': { '10': { '100': 20, '250': 29, '500': 54, '750': 79, '1000': 104 }, '20': { '100': 31, '250': 50, '500': 91, '750': 135, '1000': 178 }, '30': { '100': 50, '250': 75, '500': 141, '750': 208, '1000': 276 }, 'whole': { '100': 268, '250': 628, '500': 1246, '750': 1860, '1000': 2474 } },
      '50': { '10': { '100': 29, '250': 42, '500': 78, '750': 116, '1000': 153 }, '20': { '100': 50, '250': 74, '500': 142, '750': 210, '1000': 280 }, '30': { '100': 82, '250': 118, '500': 224, '750': 332, '1000': 440 }, 'whole': { '100': 362, '250': 852, '500': 1696, '750': 2538, '1000': 3380 } },
      '55': { '10': { '100': 44, '250': 64, '500': 121, '750': 180, '1000': 239 }, '20': { '100': 80, '250': 121, '500': 234, '750': 348, '1000': 461 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 494, '250': 1168, '500': 2328, '750': 3486, '1000': 4646 } },
      '60': { '10': { '100': 70, '250': 105, '500': 201, '750': 300, '1000': 399 }, '20': { '100': 142, '250': 215, '500': 418, '750': 624, '1000': 829 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 682, '250': 1614, '500': 3220, '750': 4826, '1000': 6432 } },
    },
  },
  {
    carrier: 'Transamerica',
    amBest: 'A',
    logo: 'ri-building-line',
    color: '#4A7C59',
    specialty: 'Best no-exam options',
    noExam: true,
    rates: {
      '25': { '10': { '100': 11, '250': 15, '500': 23, '750': 32, '1000': 41 }, '20': { '100': 14, '250': 19, '500': 31, '750': 44, '1000': 56 }, '30': { '100': 18, '250': 25, '500': 41, '750': 58, '1000': 74 }, 'whole': { '100': 102, '250': 238, '500': 468, '750': 690, '1000': 915 } },
      '30': { '10': { '100': 12, '250': 16, '500': 26, '750': 36, '1000': 47 }, '20': { '100': 15, '250': 21, '500': 34, '750': 49, '1000': 63 }, '30': { '100': 20, '250': 28, '500': 47, '750': 67, '1000': 89 }, 'whole': { '100': 122, '250': 285, '500': 562, '750': 834, '1000': 1106 } },
      '35': { '10': { '100': 13, '250': 17, '500': 30, '750': 41, '1000': 55 }, '20': { '100': 17, '250': 25, '500': 43, '750': 62, '1000': 80 }, '30': { '100': 24, '250': 33, '500': 60, '750': 87, '1000': 115 }, 'whole': { '100': 155, '250': 362, '500': 714, '750': 1060, '1000': 1408 } },
      '40': { '10': { '100': 16, '250': 23, '500': 40, '750': 58, '1000': 76 }, '20': { '100': 23, '250': 36, '500': 65, '750': 96, '1000': 126 }, '30': { '100': 34, '250': 51, '500': 94, '750': 138, '1000': 182 }, 'whole': { '100': 205, '250': 480, '500': 952, '750': 1420, '1000': 1888 } },
      '45': { '10': { '100': 22, '250': 32, '500': 59, '750': 87, '1000': 115 }, '20': { '100': 35, '250': 55, '500': 101, '750': 149, '1000': 197 }, '30': { '100': 55, '250': 82, '500': 155, '750': 228, '1000': 303 }, 'whole': { '100': 275, '250': 645, '500': 1282, '750': 1916, '1000': 2552 } },
      '50': { '10': { '100': 32, '250': 46, '500': 86, '750': 128, '1000': 169 }, '20': { '100': 55, '250': 82, '500': 157, '750': 232, '1000': 309 }, '30': { '100': 90, '250': 130, '500': 246, '750': 364, '1000': 484 }, 'whole': { '100': 372, '250': 876, '500': 1744, '750': 2610, '1000': 3478 } },
      '55': { '10': { '100': 48, '250': 71, '500': 134, '750': 199, '1000': 264 }, '20': { '100': 88, '250': 133, '500': 257, '750': 382, '1000': 507 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 508, '250': 1202, '500': 2396, '750': 3588, '1000': 4782 } },
      '60': { '10': { '100': 76, '250': 114, '500': 218, '750': 325, '1000': 432 }, '20': { '100': 152, '250': 230, '500': 448, '750': 668, '1000': 888 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 702, '250': 1662, '500': 3316, '750': 4970, '1000': 6624 } },
    },
  },
  {
    carrier: 'Mutual of Omaha',
    amBest: 'A+',
    logo: 'ri-heart-pulse-line',
    color: '#8B4513',
    specialty: 'Best for seniors & health conditions',
    noExam: true,
    rates: {
      '25': { '10': { '100': 11, '250': 15, '500': 24, '750': 33, '1000': 42 }, '20': { '100': 14, '250': 20, '500': 32, '750': 45, '1000': 58 }, '30': { '100': 19, '250': 26, '500': 43, '750': 61, '1000': 78 }, 'whole': { '100': 105, '250': 244, '500': 480, '750': 708, '1000': 938 } },
      '30': { '10': { '100': 12, '250': 16, '500': 27, '750': 37, '1000': 48 }, '20': { '100': 15, '250': 22, '500': 35, '750': 50, '1000': 65 }, '30': { '100': 21, '250': 29, '500': 49, '750': 70, '1000': 93 }, 'whole': { '100': 126, '250': 294, '500': 580, '750': 860, '1000': 1142 } },
      '35': { '10': { '100': 13, '250': 18, '500': 31, '750': 43, '1000': 57 }, '20': { '100': 18, '250': 26, '500': 44, '750': 64, '1000': 84 }, '30': { '100': 25, '250': 35, '500': 62, '750': 90, '1000': 120 }, 'whole': { '100': 160, '250': 374, '500': 738, '750': 1096, '1000': 1456 } },
      '40': { '10': { '100': 17, '250': 24, '500': 42, '750': 61, '1000': 80 }, '20': { '100': 24, '250': 38, '500': 68, '750': 100, '1000': 132 }, '30': { '100': 36, '250': 54, '500': 98, '750': 144, '1000': 192 }, 'whole': { '100': 212, '250': 496, '500': 984, '750': 1468, '1000': 1954 } },
      '45': { '10': { '100': 23, '250': 34, '500': 62, '750': 91, '1000': 120 }, '20': { '100': 37, '250': 58, '500': 106, '750': 156, '1000': 208 }, '30': { '100': 58, '250': 87, '500': 164, '750': 242, '1000': 322 }, 'whole': { '100': 285, '250': 668, '500': 1328, '750': 1984, '1000': 2642 } },
      '50': { '10': { '100': 34, '250': 49, '500': 91, '750': 135, '1000': 179 }, '20': { '100': 58, '250': 87, '500': 166, '750': 246, '1000': 328 }, '30': { '100': 95, '250': 138, '500': 261, '750': 386, '1000': 514 }, 'whole': { '100': 386, '250': 908, '500': 1808, '750': 2706, '1000': 3606 } },
      '55': { '10': { '100': 51, '250': 75, '500': 142, '750': 211, '1000': 280 }, '20': { '100': 93, '250': 141, '500': 273, '750': 406, '1000': 539 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 526, '250': 1244, '500': 2480, '750': 3714, '1000': 4950 } },
      '60': { '10': { '100': 81, '250': 121, '500': 232, '750': 346, '1000': 460 }, '20': { '100': 162, '250': 245, '500': 477, '750': 712, '1000': 947 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 728, '250': 1722, '500': 3436, '750': 5150, '1000': 6864 } },
    },
  },
  {
    carrier: 'North American Company',
    amBest: 'A+',
    logo: 'ri-map-pin-2-line',
    color: '#5B4A8A',
    specialty: 'Competitive IUL & whole life',
    noExam: false,
    rates: {
      '25': { '10': { '100': 10, '250': 14, '500': 22, '750': 31, '1000': 39 }, '20': { '100': 13, '250': 19, '500': 30, '750': 43, '1000': 54 }, '30': { '100': 18, '250': 24, '500': 40, '750': 57, '1000': 72 }, 'whole': { '100': 99, '250': 230, '500': 452, '750': 667, '1000': 884 } },
      '30': { '10': { '100': 11, '250': 15, '500': 25, '750': 34, '1000': 44 }, '20': { '100': 14, '250': 21, '500': 33, '750': 47, '1000': 61 }, '30': { '100': 20, '250': 27, '500': 46, '750': 65, '1000': 87 }, 'whole': { '100': 119, '250': 278, '500': 548, '750': 812, '1000': 1078 } },
      '35': { '10': { '100': 12, '250': 17, '500': 29, '750': 40, '1000': 53 }, '20': { '100': 16, '250': 24, '500': 41, '750': 60, '1000': 78 }, '30': { '100': 23, '250': 32, '500': 58, '750': 84, '1000': 112 }, 'whole': { '100': 148, '250': 345, '500': 681, '750': 1011, '1000': 1342 } },
      '40': { '10': { '100': 15, '250': 22, '500': 39, '750': 57, '1000': 74 }, '20': { '100': 22, '250': 35, '500': 63, '750': 93, '1000': 122 }, '30': { '100': 33, '250': 50, '500': 91, '750': 134, '1000': 178 }, 'whole': { '100': 196, '250': 458, '500': 908, '750': 1354, '1000': 1800 } },
      '45': { '10': { '100': 21, '250': 31, '500': 57, '750': 84, '1000': 111 }, '20': { '100': 33, '250': 53, '500': 97, '750': 143, '1000': 190 }, '30': { '100': 53, '250': 79, '500': 149, '750': 220, '1000': 292 }, 'whole': { '100': 262, '250': 614, '500': 1220, '750': 1822, '1000': 2426 } },
      '50': { '10': { '100': 31, '250': 45, '500': 84, '750': 124, '1000': 164 }, '20': { '100': 53, '250': 79, '500': 152, '750': 225, '1000': 299 }, '30': { '100': 88, '250': 127, '500': 240, '750': 355, '1000': 472 }, 'whole': { '100': 355, '250': 836, '500': 1664, '750': 2490, '1000': 3318 } },
      '55': { '10': { '100': 47, '250': 69, '500': 130, '750': 193, '1000': 256 }, '20': { '100': 86, '250': 130, '500': 251, '750': 373, '1000': 495 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 484, '250': 1144, '500': 2280, '750': 3414, '1000': 4550 } },
      '60': { '10': { '100': 74, '250': 111, '500': 213, '750': 318, '1000': 423 }, '20': { '100': 148, '250': 224, '500': 436, '750': 651, '1000': 866 }, '30': { '100': 0, '250': 0, '500': 0, '750': 0, '1000': 0 }, 'whole': { '100': 670, '250': 1585, '500': 3162, '750': 4738, '1000': 6316 } },
    },
  },
];

const HEALTH_MULTIPLIERS: Record<HealthClass, number> = {
  preferred_plus: 1.0,
  preferred: 1.18,
  standard_plus: 1.38,
  standard: 1.62,
};

const HEALTH_LABELS: Record<HealthClass, string> = {
  preferred_plus: 'Preferred Plus (Excellent)',
  preferred: 'Preferred (Very Good)',
  standard_plus: 'Standard Plus (Good)',
  standard: 'Standard (Average)',
};

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'How do I get life insurance quotes in Indianapolis?',
    a: 'Getting life insurance quotes in Indianapolis is simple with an independent agent like Hoosier Life Insurance. We compare rates from 20+ A-rated carriers simultaneously — Banner Life, Protective, Pacific Life, Transamerica, Mutual of Omaha, and more — and present you with a ranked comparison in a single call. You can also use our interactive rate tool above to see estimated rates by age, coverage amount, and term length before you call. The entire process takes 15–30 minutes, and you can get approved in as little as 24 hours.',
  },
  {
    q: 'What is the cheapest life insurance in Indianapolis?',
    a: 'The cheapest life insurance in Indianapolis is term life insurance. A healthy 35-year-old Indianapolis resident can get $500,000 of 20-year term coverage for approximately $30–$38/month depending on the carrier. Banner Life and Legal & General consistently offer the lowest term life rates in Indiana. For seniors or those with health conditions, Mutual of Omaha and Transamerica often offer the most competitive rates. The cheapest option for any individual depends on age, health class, coverage amount, and term length — which is why comparing multiple carriers is essential.',
  },
  {
    q: 'What is the difference between term and whole life insurance quotes in Indianapolis?',
    a: 'Term life insurance quotes in Indianapolis are significantly lower than whole life quotes for the same death benefit. A 35-year-old can get $500,000 of 20-year term coverage for ~$38/month, while the same $500,000 of whole life coverage costs ~$660–$700/month. The key difference: term life provides pure death benefit protection for a set period (10–30 years), while whole life provides lifetime coverage and builds guaranteed cash value. For most Indianapolis families, term life is the right choice during working years. Whole life makes sense for estate planning, business succession, or building tax-free wealth.',
  },
  {
    q: 'How much life insurance do Indianapolis residents need?',
    a: 'Most Indianapolis financial advisors recommend 10–12 times your annual income in life insurance coverage. For a family earning $75,000/year, that means $750,000–$900,000 in coverage. You should also factor in: your mortgage balance (the average Indianapolis home is $285,000), outstanding debts, future college costs ($120,000–$200,000 per child at Indiana universities), and the number of years until your youngest child is financially independent. Our free consultation includes a personalized needs analysis to determine the right coverage amount for your specific situation.',
  },
  {
    q: 'What health class will I qualify for in Indianapolis?',
    a: 'Life insurance carriers use health classifications to determine your rate. Preferred Plus (best rate) requires excellent health, no tobacco use, ideal BMI, clean family history, and no significant medical conditions. Preferred requires very good health with minor issues. Standard Plus covers good health with some controlled conditions. Standard covers average health. Most Indianapolis residents qualify for Preferred or Standard Plus. Conditions like controlled high blood pressure, type 2 diabetes, or a history of cancer don\'t automatically disqualify you — they just affect which health class you qualify for. We work with carriers that specialize in impaired risk cases.',
  },
  {
    q: 'Can I get life insurance quotes without a medical exam in Indianapolis?',
    a: 'Yes. No-exam life insurance is available for Indianapolis residents. Transamerica and Mutual of Omaha offer no-exam term life coverage up to $500,000 with simplified underwriting — just answer health questions online or over the phone. Approval typically happens within 24–48 hours. No-exam policies cost 10–20% more than fully underwritten policies, but the speed and convenience make them popular with busy Indianapolis professionals. For coverage above $500,000, a brief medical exam (blood draw and vitals) is typically required.',
  },
  {
    q: 'Which life insurance carrier is best for Indianapolis residents?',
    a: 'The best life insurance carrier for Indianapolis residents depends on your specific situation. For lowest term life rates: Banner Life / Legal & General. For best 30-year term rates: Protective Life. For high-income earners and large policies: Pacific Life. For no-exam options: Transamerica. For seniors and health conditions: Mutual of Omaha. For IUL and whole life: North American Company. As an independent Indianapolis agent, we compare all of these carriers simultaneously and recommend the best fit for your age, health, and coverage needs — not the carrier that pays us the highest commission.',
  },
  {
    q: 'How long does it take to get life insurance in Indianapolis?',
    a: 'The timeline depends on the type of policy. No-exam term life: 24–48 hours from application to approval. Fully underwritten term life: 2–6 weeks (includes medical exam scheduling, lab results, and underwriting review). Whole life and universal life: 3–6 weeks. The fastest path is a no-exam policy if you qualify. For fully underwritten policies, we manage the entire process — scheduling your exam, following up with the carrier, and keeping you updated throughout. Most Indianapolis clients are covered within 3–4 weeks.',
  },
  {
    q: 'Are life insurance quotes free in Indianapolis?',
    a: 'Yes — life insurance quotes are always free. There is never a cost to compare rates or speak with a licensed Indianapolis agent. Hoosier Life Insurance is compensated by the insurance carrier when you purchase a policy, not by charging you a fee. Our quotes are no-obligation — you can compare rates from 20+ carriers and walk away with no cost or commitment. We encourage Indianapolis residents to shop around and compare before making a decision.',
  },
  {
    q: 'What information do I need to get a life insurance quote in Indianapolis?',
    a: 'To get an accurate life insurance quote in Indianapolis, you\'ll need: (1) Your date of birth and gender. (2) Your height and weight. (3) Your tobacco use status (current, former, or never). (4) Basic health history — any major conditions, medications, or surgeries in the past 5–10 years. (5) Your desired coverage amount and term length. (6) Your beneficiary information (name and relationship). That\'s it. The entire quote process takes 10–15 minutes over the phone or video call. No paperwork, no office visit required.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'Ryan & Melissa T.',
    location: 'Fishers, IN',
    quote: 'We called three different agents and got three different quotes. Benjamin compared 8 carriers in one call and found us $750K of 30-year term for $62/month combined. Saved us $40/month vs. what another agent quoted.',
    stars: 5,
  },
  {
    name: 'Patricia W.',
    location: 'Carmel, IN',
    quote: 'I\'m 58 and was worried I\'d be uninsurable. Benjamin found me $250K of whole life coverage with Mutual of Omaha for $142/month — no medical exam. The process took less than a week.',
    stars: 5,
  },
  {
    name: 'James & Kendra O.',
    location: 'Noblesville, IN',
    quote: 'We had no idea how much we were underinsured. Our employer coverage was $150K combined. Benjamin showed us we needed $1.2M and found it for $104/month. Best financial decision we\'ve made.',
    stars: 5,
  },
  {
    name: 'Carlos M.',
    location: 'Indianapolis, IN',
    quote: 'Self-employed, so no employer coverage at all. Benjamin compared 6 carriers and got me $500K of 20-year term for $38/month. The whole thing was done over the phone — no office visit needed.',
    stars: 5,
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function LifeInsuranceQuotesIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Quote tool state
  const [age, setAge] = useState<AgeKey>('35');
  const [coverage, setCoverage] = useState<CoverageAmt>('500');
  const [termLen, setTermLen] = useState<TermLen>('20');
  const [healthClass, setHealthClass] = useState<HealthClass>('preferred_plus');
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Life Insurance Quotes Indianapolis IN | Compare 20+ Carriers | Hoosier Life';
    const description = 'Get free life insurance quotes in Indianapolis, IN. Compare rates from 20+ A-rated carriers — Banner Life, Protective, Pacific Life, Transamerica & more. Independent agent, no obligation. Call (317) 648-2983.';
    const keywords = 'life insurance quotes Indianapolis, life insurance quotes Indianapolis IN, Indianapolis life insurance quotes, compare life insurance Indianapolis, life insurance rates Indianapolis Indiana, cheap life insurance quotes Indianapolis';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/3d5b8b768cb182a9f8799263e2bb6c3d.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-15T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        about: [
          { '@type': 'Thing', name: 'Life Insurance Quotes Indianapolis' },
          { '@type': 'Thing', name: 'Compare Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Indianapolis Life Insurance Rates' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance Quotes Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Life Insurance Quote Comparison',
        name: 'Life Insurance Quotes Indianapolis',
        description: 'Free life insurance quote comparison for Indianapolis and Marion County residents. Compare rates from 20+ A-rated carriers. Independent agent — no obligation, no pressure.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'City', name: 'Westfield' },
          { '@type': 'City', name: 'Zionsville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  // Compute ranked carrier results
  const rankedResults = useMemo(() => {
    return CARRIERS
      .map(carrier => {
        const baseRate = carrier.rates[age]?.[termLen]?.[coverage] ?? 0;
        const adjustedRate = Math.round(baseRate * HEALTH_MULTIPLIERS[healthClass]);
        return { ...carrier, monthlyRate: adjustedRate };
      })
      .filter(c => c.monthlyRate > 0)
      .sort((a, b) => a.monthlyRate - b.monthlyRate);
  }, [age, coverage, termLen, healthClass]);

  const coverageLabel = (c: CoverageAmt) => c === '1000' ? '$1,000,000' : `$${c},000`;
  const termLabel = (t: TermLen) => t === 'whole' ? 'Whole Life' : `${t}-Year Term`;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Quotes &amp; Rates</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20Indianapolis%20Indiana%20financial%20planning%20office%20interior%20with%20large%20windows%20overlooking%20downtown%20Indianapolis%20skyline%20warm%20professional%20lighting%20clean%20minimalist%20desk%20with%20laptop%20showing%20insurance%20comparison%20charts%20confident%20professional%20environment%20representing%20life%20insurance%20quote%20comparison%20service%20Marion%20County%20Indiana%20corporate%20photography&width=1440&height=680&seq=liq-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance Quotes Indianapolis Indiana — Compare 20+ Carriers"
            title="Life Insurance Quotes in Indianapolis, Indiana — Free Multi-Carrier Comparison"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={680}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/97 via-[#1A3A52]/90 to-[#1A3A52]/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-[680px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-search-line text-sm"></i>
              Indianapolis &amp; Marion County · 20+ Carriers Compared
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance Quotes in{' '}
              <span className="text-[#D97642]">Indianapolis, Indiana</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-[600px]">
              Compare rates from <strong className="text-white">20+ A-rated carriers</strong> side by side — Banner Life, Protective, Pacific Life, Transamerica, Mutual of Omaha &amp; more. One call. Every option. Best rate guaranteed.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-money-dollar-circle-line', text: 'From $15/mo' },
                { icon: 'ri-search-line', text: '20+ carriers compared' },
                { icon: 'ri-time-line', text: 'Quotes in 15 minutes' },
                { icon: 'ri-shield-check-line', text: 'No obligation, ever' },
                { icon: 'ri-map-pin-fill', text: 'Local Indianapolis agent' },
              ].map(badge => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
                  <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-xs text-white font-semibold whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowBookingModal(true)}
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-lg"></i>
                Compare My Rates Free
              </button>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '20+', label: 'A-rated carriers compared in a single call' },
              { value: '15 min', label: 'Average time to get your personalized quote' },
              { value: '$0', label: 'Cost to compare — quotes are always free' },
              { value: '8–12%', label: 'Indiana rates below the national average' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Life Insurance Quotes in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'A healthy 35-year-old Indianapolis resident gets $500K of 20-year term coverage for ~$30–$38/month depending on carrier',
                  'Indiana life insurance rates are 8–12% below the national average — Hoosiers pay less for the same coverage',
                  'Quotes are always free — Hoosier Life is compensated by the carrier, never by charging you a fee',
                  'Independent agents compare 20+ carriers; captive agents (State Farm, Allstate) can only quote one company',
                  'No-exam life insurance is available — approved in 24–48 hours, no doctor visit required',
                  'Rates are locked in at purchase — a 35-year-old who buys today pays the same rate at age 55',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── INTERACTIVE RATE COMPARISON TOOL ─────────────────────── */}
            <div className="mb-16">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Interactive Rate Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Life Insurance Rates in Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, term length, and health class to see estimated rates from top carriers ranked lowest to highest. Rates shown are for non-tobacco users.
              </p>

              {/* Tool Card */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Controls */}
                <div className="bg-[#1A3A52] px-7 py-6">
                  <h3 className="text-base font-bold text-white mb-5 flex items-center gap-2">
                    <i className="ri-equalizer-line text-[#D97642]"></i>
                    Customize Your Quote
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Age */}
                    <div>
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">Your Age</label>
                      <div className="flex flex-wrap gap-1.5">
                        {(['25', '30', '35', '40', '45', '50', '55', '60'] as AgeKey[]).map(a => (
                          <button
                            key={a}
                            onClick={() => { setAge(a); setShowResults(false); }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${age === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Coverage */}
                    <div>
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">Coverage Amount</label>
                      <div className="flex flex-wrap gap-1.5">
                        {([['100', '$100K'], ['250', '$250K'], ['500', '$500K'], ['750', '$750K'], ['1000', '$1M']] as [CoverageAmt, string][]).map(([val, label]) => (
                          <button
                            key={val}
                            onClick={() => { setCoverage(val); setShowResults(false); }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${coverage === val ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Term */}
                    <div>
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">Policy Type / Term</label>
                      <div className="flex flex-wrap gap-1.5">
                        {([['10', '10-Year'], ['20', '20-Year'], ['30', '30-Year'], ['whole', 'Whole Life']] as [TermLen, string][]).map(([val, label]) => (
                          <button
                            key={val}
                            onClick={() => { setTermLen(val); setShowResults(false); }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${termLen === val ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Health Class */}
                    <div>
                      <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">Health Class</label>
                      <div className="flex flex-col gap-1.5">
                        {(Object.entries(HEALTH_LABELS) as [HealthClass, string][]).map(([val, label]) => (
                          <button
                            key={val}
                            onClick={() => { setHealthClass(val); setShowResults(false); }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer text-left whitespace-nowrap ${healthClass === val ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowResults(true)}
                    className="mt-6 w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-search-line text-lg"></i>
                    Compare Rates from {CARRIERS.length} Carriers
                  </button>
                </div>

                {/* Results */}
                {showResults && (
                  <div className="px-7 py-6">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h3 className="text-base font-bold text-[#1A3A52]">
                          {rankedResults.length} Carriers — Age {age}, {coverageLabel(coverage)}, {termLabel(termLen)}
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">{HEALTH_LABELS[healthClass]} · Non-tobacco · Indianapolis, IN</p>
                      </div>
                      <span className="text-xs bg-[#D97642]/10 text-[#D97642] font-semibold px-3 py-1 rounded-full whitespace-nowrap">Ranked: Lowest First</span>
                    </div>

                    {rankedResults.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <i className="ri-information-line text-3xl mb-2 block"></i>
                        <p className="text-sm">No rates available for this combination. Try a different age or term length.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {rankedResults.map((carrier, idx) => (
                          <div
                            key={carrier.carrier}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${idx === 0 ? 'border-[#D97642]/40 bg-[#FFF4ED]' : 'border-gray-100 bg-[#F9F7F4]'}`}
                          >
                            {/* Rank */}
                            <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 font-extrabold text-sm ${idx === 0 ? 'bg-[#D97642] text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>
                              {idx + 1}
                            </div>

                            {/* Icon */}
                            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${carrier.color}15` }}>
                              <i className={`${carrier.logo} text-xl`} style={{ color: carrier.color }}></i>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-[#1A3A52] truncate">{carrier.carrier}</span>
                                <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {carrier.amBest}</span>
                                {carrier.noExam && (
                                  <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">No-Exam Available</span>
                                )}
                                {idx === 0 && (
                                  <span className="text-xs bg-[#D97642] text-white font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Lowest Rate</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{carrier.specialty}</p>
                            </div>

                            {/* Rate */}
                            <div className="text-right flex-shrink-0">
                              <div className="text-xl font-extrabold text-[#D97642]">${carrier.monthlyRate}<span className="text-sm font-normal text-gray-400">/mo</span></div>
                              <div className="text-xs text-gray-400">${(carrier.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-5 p-4 bg-[#1A3A52]/5 rounded-xl">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates shown are estimates for non-tobacco users based on the selected health class. Actual premiums depend on full underwriting, medical history, and carrier-specific guidelines. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized quote.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {!showResults && (
                  <div className="px-7 py-8 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-search-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your options above and click <strong className="text-[#1A3A52]">Compare Rates</strong> to see carrier rankings.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Why Independent vs. Captive */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Independent Agent vs. Captive Agent: Why It Matters for Indianapolis Quotes
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                When you call State Farm, Allstate, or Farmers for a life insurance quote in Indianapolis, you're only getting one company's rate. When you call Hoosier Life Insurance, you get rates from 20+ carriers simultaneously — and we're legally required to recommend the best option for you, not the one that pays us the most.
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-sm font-bold text-white">Independent vs. Captive Agent Comparison</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Feature</th>
                        <th className="px-5 py-3 text-center font-semibold text-[#D97642]">Hoosier Life (Independent)</th>
                        <th className="px-5 py-3 text-center font-semibold text-gray-400">Captive Agent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        ['Carriers compared', '20+ A-rated carriers', '1 carrier only'],
                        ['Legally required to find best rate', '✓ Yes (fiduciary standard)', '✗ No'],
                        ['Can shop if your health changes', '✓ Yes — re-shop anytime', '✗ No'],
                        ['No-exam options available', '✓ Multiple carriers', '✗ Limited'],
                        ['Impaired risk specialists', '✓ Yes', '✗ Rarely'],
                        ['Cost to you', '$0 — carrier pays us', '$0 — carrier pays them'],
                      ].map(([feature, indie, captive], i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                          <td className="px-5 py-3 font-medium text-gray-700">{feature}</td>
                          <td className="px-5 py-3 text-center text-[#1A3A52] font-semibold">{indie}</td>
                          <td className="px-5 py-3 text-center text-gray-400">{captive}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-[#FFF4ED] border-l-4 border-[#D97642] rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-xl text-[#D97642] flex-shrink-0 mt-0.5"></i>
                  <div>
                    <h4 className="text-sm font-bold text-[#1A3A52] mb-1">The Indianapolis Rate Gap</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      For a 40-year-old Indianapolis resident seeking $500,000 of 20-year term coverage, the difference between the highest and lowest carrier rate can be <strong className="text-[#1A3A52]">$30–$50/month</strong> — or $7,200–$12,000 over the life of the policy. That's why comparing multiple carriers isn't optional — it's essential.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Carrier Profiles */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Carrier Profiles</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Top Life Insurance Carriers for Indianapolis Residents
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {CARRIERS.map(carrier => (
                  <div key={carrier.carrier} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${carrier.color}15` }}>
                        <i className={`${carrier.logo} text-2xl`} style={{ color: carrier.color }}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-sm font-bold text-[#1A3A52]">{carrier.carrier}</h3>
                          <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {carrier.amBest}</span>
                        </div>
                        <p className="text-xs font-semibold text-[#D97642] mb-2">{carrier.specialty}</p>
                        {carrier.noExam && (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                            <i className="ri-checkbox-circle-line text-xs"></i>
                            No-Exam Available
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Health Classes Explained */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Understanding Your Rate</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Health Classes &amp; How They Affect Your Indianapolis Quote
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Life insurance carriers classify applicants into health tiers that directly determine your monthly premium. Here's what each class means and what Indianapolis residents typically qualify for:
              </p>
              <div className="flex flex-col gap-4">
                {[
                  {
                    class: 'Preferred Plus',
                    multiplier: '1.0x (best rate)',
                    color: '#4A7C59',
                    bg: '#F0FFF4',
                    border: '#4A7C59',
                    criteria: [
                      'Excellent health — no significant medical conditions',
                      'BMI within ideal range (typically 18.5–27)',
                      'No tobacco use in past 5 years',
                      'Clean family history (no early heart disease or cancer)',
                      'Blood pressure and cholesterol within normal range',
                    ],
                    note: 'Approximately 20–25% of Indianapolis applicants qualify for this class.',
                  },
                  {
                    class: 'Preferred',
                    multiplier: '~1.18x base rate',
                    color: '#2E6B8A',
                    bg: '#F0F8FF',
                    border: '#2E6B8A',
                    criteria: [
                      'Very good health with minor issues',
                      'Slightly elevated BMI or cholesterol (controlled)',
                      'No tobacco use in past 3 years',
                      'Minor family history concerns',
                    ],
                    note: 'The most common class for healthy Indianapolis adults aged 30–50.',
                  },
                  {
                    class: 'Standard Plus',
                    multiplier: '~1.38x base rate',
                    color: '#D97642',
                    bg: '#FFF4ED',
                    border: '#D97642',
                    criteria: [
                      'Good health with some controlled conditions',
                      'Controlled high blood pressure or cholesterol',
                      'BMI slightly above ideal range',
                      'Former tobacco use (quit 1–3 years ago)',
                    ],
                    note: 'Common for Indianapolis residents with well-managed health conditions.',
                  },
                  {
                    class: 'Standard',
                    multiplier: '~1.62x base rate',
                    color: '#8B4513',
                    bg: '#FFF8F0',
                    border: '#8B4513',
                    criteria: [
                      'Average health — some uncontrolled conditions',
                      'Higher BMI, elevated blood pressure',
                      'Type 2 diabetes (well-controlled)',
                      'Recent tobacco use (quit less than 1 year ago)',
                    ],
                    note: 'Still insurable — Mutual of Omaha and Transamerica specialize in standard-class applicants.',
                  },
                ].map(item => (
                  <div key={item.class} className="rounded-xl border p-5" style={{ backgroundColor: item.bg, borderColor: `${item.border}30` }}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <h3 className="text-base font-bold" style={{ color: item.color }}>{item.class}</h3>
                        <p className="text-xs font-semibold text-gray-500">{item.multiplier}</p>
                      </div>
                    </div>
                    <ul className="flex flex-col gap-1.5 mb-3">
                      {item.criteria.map(c => (
                        <li key={c} className="flex items-start gap-2 text-sm text-gray-600">
                          <i className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5 text-xs" style={{ color: item.color }}></i>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-500 italic">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Reviews</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                What Indianapolis Residents Say About Our Quote Process
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What You Need to Get a Quote */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Getting Started</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                What You Need to Get a Life Insurance Quote in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Getting a life insurance quote in Indianapolis is simpler than most people expect. Here's exactly what you need — and what you don't:
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-[#4A7C59] text-lg"></i>
                    What You Need
                  </h3>
                  <ul className="flex flex-col gap-3">
                    {[
                      { icon: 'ri-calendar-line', text: 'Date of birth and gender' },
                      { icon: 'ri-body-scan-line', text: 'Height and weight' },
                      { icon: 'ri-forbid-line', text: 'Tobacco use status (current, former, never)' },
                      { icon: 'ri-heart-pulse-line', text: 'Basic health history (major conditions, medications)' },
                      { icon: 'ri-money-dollar-circle-line', text: 'Desired coverage amount and term length' },
                      { icon: 'ri-user-heart-line', text: 'Beneficiary name and relationship' },
                    ].map(item => (
                      <li key={item.text} className="flex items-start gap-3 text-sm text-gray-600">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#4A7C59]/10 flex-shrink-0">
                          <i className={`${item.icon} text-sm text-[#4A7C59]`}></i>
                        </div>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                    <i className="ri-close-circle-fill text-red-400 text-lg"></i>
                    What You Don't Need
                  </h3>
                  <ul className="flex flex-col gap-3">
                    {[
                      { icon: 'ri-hospital-line', text: 'A doctor\'s visit or physical exam (for most policies)' },
                      { icon: 'ri-file-paper-line', text: 'Paperwork or forms before the call' },
                      { icon: 'ri-building-line', text: 'An office visit — everything is done by phone or video' },
                      { icon: 'ri-bank-card-line', text: 'Payment information to get a quote' },
                      { icon: 'ri-time-line', text: 'Hours of your time — quotes take 15–30 minutes' },
                      { icon: 'ri-user-settings-line', text: 'Insurance knowledge — we explain everything in plain English' },
                    ].map(item => (
                      <li key={item.text} className="flex items-start gap-3 text-sm text-gray-600">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 flex-shrink-0">
                          <i className={`${item.icon} text-sm text-red-400`}></i>
                        </div>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance Quotes Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-liq-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-search-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Indianapolis Quote</h3>
                    <p className="text-xs text-white/70">20+ carriers · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Results in 15 min</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Results in 15 minutes</p>
              </div>
            </div>

            {/* Quick Rate Snapshot */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-[#D97642]"></i>
                Sample Rates — Age 35, Indianapolis
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { label: '$250K · 20-Year Term', rate: '$22/mo', carrier: 'Banner Life' },
                  { label: '$500K · 20-Year Term', rate: '$38/mo', carrier: 'Banner Life' },
                  { label: '$1M · 20-Year Term', rate: '$72/mo', carrier: 'Banner Life' },
                  { label: '$500K · 30-Year Term', rate: '$54/mo', carrier: 'Protective Life' },
                  { label: '$500K · Whole Life', rate: '$660/mo', carrier: 'North American' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-semibold text-[#1A3A52]">{item.label}</p>
                      <p className="text-xs text-gray-400">{item.carrier} · Preferred Plus</p>
                    </div>
                    <span className="text-base font-extrabold text-[#D97642] whitespace-nowrap ml-3">{item.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Estimates only. Call for your exact rate.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Quote Call</h3>
                    <p className="text-xs text-white/70">We compare 20+ carriers live on the call</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Quote Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Quote Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/life-insurance-indianapolis', label: 'Life Insurance Indianapolis', icon: 'ri-shield-check-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/whole-life-insurance-indianapolis', label: 'Whole Life Insurance Indianapolis', icon: 'ri-infinity-line' },
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-pulse-line' },
                  { to: '/instant-quote', label: 'Instant Online Quote Tool', icon: 'ri-file-list-3-line' },
                  { to: '/compare', label: 'Compare Policy Types', icon: 'ri-scales-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20navy%20blue%20blazer%20and%20white%20shirt%20warm%20confident%20smile%20direct%20eye%20contact%20clean%20white%20studio%20background%20professional%20corporate%20headshot%20photography%20approachable%20trustworthy%20demeanor%20financial%20advisor%20portrait%20Indianapolis%20Indiana&width=600&height=700&seq=agent-bwright-liq-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — Licensed Life Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Life Insurance Quote Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-search-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Independent Agent</div>
                    <div className="text-base font-bold text-[#1A3A52]">20+ Carriers</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Quote Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                As an independent life insurance agent serving Indianapolis and Central Indiana for over 12 years, I've helped hundreds of Marion County families compare rates from 20+ carriers and find the right coverage at the right price.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Unlike captive agents who can only quote one company, I compare Banner Life, Protective, Pacific Life, Transamerica, Mutual of Omaha, and more — live on our call — so you see every option side by side. No jargon, no pressure, no hidden fees.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My Free Quote
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Compare all types of life insurance available to Indianapolis residents</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/term-life-insurance-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Insurance Indianapolis', desc: 'Affordable coverage from $15/mo — 10, 20, and 30-year terms for Marion County families' },
              { to: '/whole-life-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Whole Life Insurance Indianapolis', desc: 'Permanent coverage with guaranteed cash value growth — lifetime protection' },
              { to: '/final-expense-insurance-indianapolis', icon: 'ri-heart-pulse-line', title: 'Final Expense Insurance Indianapolis', desc: 'No-exam burial coverage for Indianapolis seniors from $30/mo' },
              { to: '/mortgage-protection-insurance-indianapolis', icon: 'ri-home-heart-line', title: 'Mortgage Protection Indianapolis', desc: 'Ensure your family keeps the home if something happens to you' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Compare Indianapolis life insurance quotes — free, in 15 minutes.
          </h2>
          <p className="text-base text-white/75 mb-7">
            20+ A-rated carriers compared side by side. No obligation, no pressure. Local Indianapolis agent who works for you — not the insurance company.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowBookingModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-lg"></i>
              Compare My Rates Free
            </button>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Life Insurance Quotes Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Life Insurance Quotes" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Free Indianapolis Life Insurance Quote</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
